import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Check,
  X,
  CreditCard,
  FileText,
  Calendar,
  Building2,
  Receipt,
  ExternalLink,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { ExpenseModal } from '@/components/travel/expenses/expense-modal';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentAccountSelect } from '@/components/travel/accounting/payment-account-select';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAID: 'bg-green-100 text-green-700',
};

const statusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  APPROVED: 'status.approved',
  REJECTED: 'status.rejected',
  PAID: 'status.paid',
};

const categoryLabelKeys: Record<string, string> = {
  SUPPLIER_PAYMENT: 'category.supplierPayment',
  SALARY: 'category.salary',
  RENT: 'category.rent',
  UTILITIES: 'category.utilities',
  MARKETING: 'category.marketing',
  TRAVEL: 'category.travel',
  OFFICE_SUPPLIES: 'category.officeSupplies',
  INSURANCE: 'category.insurance',
  TAXES: 'category.taxes',
  OTHER: 'category.other',
};

type PaymentMethod =
  | 'CREDIT_CARD'
  | 'DEBIT_CARD'
  | 'BANK_TRANSFER'
  | 'PAYPAL'
  | 'CASH'
  | 'CHEQUE'
  | 'OTHER';

export function ExpenseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('expenses');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState<{ open: boolean; reason: string }>({
    open: false,
    reason: '',
  });
  const [markPaidDialog, setMarkPaidDialog] = useState<{
    open: boolean;
    method: PaymentMethod;
    ref: string;
    accountId?: string;
  }>({
    open: false,
    method: 'BANK_TRANSFER',
    ref: '',
  });

  const {
    data: expense,
    isLoading,
    error,
  } = trpc.expense.byId.useQuery(id ?? '', {
    enabled: !!id,
  });

  const utils = trpc.useUtils();

  const approveMutation = trpc.expense.approve.useMutation({
    onSuccess: () => {
      utils.expense.byId.invalidate(id);
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.approved'));
      setApproveDialog(false);
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.approveFailed'));
    },
  });

  const rejectMutation = trpc.expense.reject.useMutation({
    onSuccess: () => {
      utils.expense.byId.invalidate(id);
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.rejected'));
      setRejectDialog({ open: false, reason: '' });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.rejectFailed'));
    },
  });

  const markPaidMutation = trpc.expense.markPaid.useMutation({
    onSuccess: () => {
      utils.expense.byId.invalidate(id);
      utils.expense.list.invalidate();
      utils.expense.stats.invalidate();
      showSuccessToast(t('toasts.markedPaid'));
      setMarkPaidDialog({ open: false, method: 'BANK_TRANSFER', ref: '' });
    },
    onError: (error) => {
      showErrorToast(error.message || t('toasts.markPaidFailed'));
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
        <span className="text-muted-foreground ml-2">{t('detail.loadingExpense')}</span>
      </div>
    );
  }

  if (error || !expense) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/travel/expenses')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.backToExpenses')}
        </Button>
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('detail.errorLoading')}</p>
          <p className="text-sm">{error?.message || t('detail.expenseNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/travel/expenses')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.back')}
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{expense.expenseNumber || t('expense')}</h1>
              <span
                className={`px-2 py-0.5 text-xs font-medium ${
                  statusColors[expense.status] || 'bg-gray-100 text-gray-700'
                }`}
              >
                {t(statusLabelKeys[expense.status] || 'status.pending')}
              </span>
            </div>
            <p className="text-muted-foreground">
              {t(categoryLabelKeys[expense.category] || 'category.other')} -{' '}
              {expense.vendorName || t('noVendor')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {expense.status === 'PENDING' && (
            <>
              <Button variant="outline" onClick={() => setIsEditModalOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Button>
              <Button variant="outline" onClick={() => setRejectDialog({ open: true, reason: '' })}>
                <X className="mr-2 h-4 w-4" />
                {t('actions.reject')}
              </Button>
              <Button onClick={() => setApproveDialog(true)}>
                <Check className="mr-2 h-4 w-4" />
                {t('actions.approve')}
              </Button>
            </>
          )}
          {expense.status === 'APPROVED' && (
            <Button
              onClick={() => setMarkPaidDialog({ open: true, method: 'BANK_TRANSFER', ref: '' })}
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {t('actions.markAsPaid')}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Amount Card */}
          <div className="border-border bg-card border p-6">
            <h3 className="mb-4 font-semibold">{t('detail.amountDetails')}</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="bg-muted p-4 text-center">
                <p className="text-muted-foreground text-sm">{t('form.amount')}</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(expense.amount, expense.currency)}
                </p>
              </div>
              <div className="bg-orange-50 p-4 text-center">
                <p className="text-muted-foreground text-sm">
                  {t('form.taxAmount')} ({expense.taxRate}%)
                </p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(expense.taxAmount, expense.currency)}
                </p>
              </div>
              <div className="bg-green-50 p-4 text-center">
                <p className="text-muted-foreground text-sm">{t('form.netAmount')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    expense.netAmount ?? expense.amount - expense.taxAmount,
                    expense.currency
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Details Card */}
          <div className="border-border bg-card border p-6">
            <h3 className="mb-4 font-semibold">{t('sidebar.expenseInfo')}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('form.category')}</p>
                <p className="font-medium">
                  {t(categoryLabelKeys[expense.category] || 'category.other')}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('detail.expenseDate')}</p>
                <p className="flex items-center gap-2 font-medium">
                  <Calendar className="text-muted-foreground h-4 w-4" />
                  {new Date(expense.expenseDate).toLocaleDateString(dateLocale)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('form.vendorName')}</p>
                <p className="font-medium">{expense.vendorName || '-'}</p>
              </div>
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('form.currency')}</p>
                <p className="font-medium">{expense.currency}</p>
              </div>
              {expense.supplierInvoiceNumber && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('form.supplierInvoice')}</p>
                  <p className="font-medium">{expense.supplierInvoiceNumber}</p>
                </div>
              )}
              {expense.supplierInvoiceDate && (
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('form.supplierInvoiceDate')}</p>
                  <p className="font-medium">
                    {new Date(expense.supplierInvoiceDate).toLocaleDateString(dateLocale)}
                  </p>
                </div>
              )}
              <div className="space-y-1">
                <p className="text-muted-foreground text-sm">{t('form.isReimbursable')}</p>
                <p className="font-medium">
                  {expense.isReimbursable ? tCommon('yes') : tCommon('no')}
                </p>
              </div>
            </div>
            {expense.description && (
              <div className="mt-4 border-t pt-4">
                <p className="text-muted-foreground mb-1 text-sm">{t('form.description')}</p>
                <p>{expense.description}</p>
              </div>
            )}
            {expense.notes && (
              <div className="mt-4 border-t pt-4">
                <p className="text-muted-foreground mb-1 text-sm">{t('form.notes')}</p>
                <p className="whitespace-pre-wrap">{expense.notes}</p>
              </div>
            )}
          </div>

          {/* Payment Details (if paid) */}
          {expense.status === 'PAID' && expense.paidAt && (
            <div className="border-border bg-card border p-6">
              <h3 className="mb-4 font-semibold">{t('detail.paymentDetails')}</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('dialogs.paymentMethod')}</p>
                  <p className="font-medium">{expense.paymentMethod || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm">{t('detail.paidAt')}</p>
                  <p className="font-medium">
                    {new Date(expense.paidAt).toLocaleDateString(dateLocale)}
                  </p>
                </div>
                {expense.paymentRef && (
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-sm">{t('dialogs.paymentRef')}</p>
                    <p className="font-medium">{expense.paymentRef}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Linked Booking */}
          {expense.booking && (
            <div className="border-border bg-card border p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Receipt className="h-4 w-4" />
                {t('sidebar.linkedBooking')}
              </h3>
              <div className="space-y-2">
                <Link
                  to={`/travel/bookings/${expense.booking.id}`}
                  className="text-primary flex items-center gap-2 hover:underline"
                >
                  {expense.booking.bookingReference}
                  <ExternalLink className="h-3 w-3" />
                </Link>
                {expense.booking.customer && (
                  <p className="text-muted-foreground text-sm">
                    {expense.booking.customer.firstName} {expense.booking.customer.lastName}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Linked Partner */}
          {expense.partner && (
            <div className="border-border bg-card border p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Building2 className="h-4 w-4" />
                {t('sidebar.linkedPartner')}
              </h3>
              <div className="space-y-2">
                <Link
                  to={`/travel/cari/accounts/partner/${expense.partner.id}`}
                  className="text-primary flex items-center gap-2 hover:underline"
                >
                  {expense.partner.companyName}
                  <ExternalLink className="h-3 w-3" />
                </Link>
                {expense.partner.contactEmail && (
                  <p className="text-muted-foreground text-sm">{expense.partner.contactEmail}</p>
                )}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="border-border bg-card border p-4">
            <h3 className="mb-3 font-semibold">{t('sidebar.timeline')}</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                    <FileText className="text-primary h-4 w-4" />
                  </div>
                  <div className="bg-border mt-2 w-px flex-1" />
                </div>
                <div className="pb-4">
                  <p className="font-medium">{t('timeline.created')}</p>
                  <p className="text-muted-foreground text-sm">
                    {new Date(expense.createdAt).toLocaleDateString(dateLocale)}{' '}
                    {new Date(expense.createdAt).toLocaleTimeString(dateLocale)}
                  </p>
                </div>
              </div>

              {expense.approvedAt && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`flex h-8 w-8 items-center justify-center ${
                        expense.status === 'REJECTED' ? 'bg-red-100' : 'bg-green-100'
                      }`}
                    >
                      {expense.status === 'REJECTED' ? (
                        <X className="h-4 w-4 text-red-600" />
                      ) : (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                    {expense.paidAt && <div className="bg-border mt-2 w-px flex-1" />}
                  </div>
                  <div className="pb-4">
                    <p className="font-medium">
                      {expense.status === 'REJECTED'
                        ? t('timeline.rejected')
                        : t('timeline.approved')}
                    </p>
                    <p className="text-muted-foreground text-sm">
                      {new Date(expense.approvedAt).toLocaleDateString(dateLocale)}{' '}
                      {new Date(expense.approvedAt).toLocaleTimeString(dateLocale)}
                    </p>
                  </div>
                </div>
              )}

              {expense.paidAt && (
                <div className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="flex h-8 w-8 items-center justify-center bg-green-100">
                      <CreditCard className="h-4 w-4 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium">{t('timeline.paid')}</p>
                    <p className="text-muted-foreground text-sm">
                      {new Date(expense.paidAt).toLocaleDateString(dateLocale)}{' '}
                      {new Date(expense.paidAt).toLocaleTimeString(dateLocale)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <ExpenseModal
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          editId={id}
          onSuccess={() => {
            utils.expense.byId.invalidate(id);
          }}
        />
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dialogs.approveTitle')}</DialogTitle>
            <DialogDescription>{t('dialogs.approveMessage')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog(false)}>
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={() => id && approveMutation.mutate({ id })}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.approve')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialog.open}
        onOpenChange={(open) => setRejectDialog({ ...rejectDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dialogs.rejectTitle')}</DialogTitle>
            <DialogDescription>{t('dialogs.rejectMessage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('dialogs.rejectReason')}</Label>
              <Textarea
                value={rejectDialog.reason}
                onChange={(e) => setRejectDialog({ ...rejectDialog, reason: e.target.value })}
                placeholder={t('dialogs.rejectPlaceholder')}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, reason: '' })}>
              {tCommon('cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => id && rejectMutation.mutate({ id, reason: rejectDialog.reason })}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog
        open={markPaidDialog.open}
        onOpenChange={(open) => setMarkPaidDialog({ ...markPaidDialog, open })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dialogs.markPaidTitle')}</DialogTitle>
            <DialogDescription>{t('dialogs.markPaidMessage')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('dialogs.paymentMethod')}</Label>
              <Combobox
                options={[
                  { value: 'BANK_TRANSFER', label: t('paymentMethods.bankTransfer') },
                  { value: 'CREDIT_CARD', label: t('paymentMethods.creditCard') },
                  { value: 'DEBIT_CARD', label: t('paymentMethods.debitCard') },
                  { value: 'CASH', label: t('paymentMethods.cash') },
                  { value: 'CHEQUE', label: t('paymentMethods.cheque') },
                  { value: 'PAYPAL', label: t('paymentMethods.paypal') },
                  { value: 'OTHER', label: t('paymentMethods.other') },
                ]}
                value={markPaidDialog.method}
                onValueChange={(value) =>
                  setMarkPaidDialog({ ...markPaidDialog, method: value as PaymentMethod })
                }
                placeholder={t('dialogs.selectMethod')}
                searchPlaceholder={tCommon('search')}
              />
            </div>
            <PaymentAccountSelect
              value={markPaidDialog.accountId}
              onChange={(v) => setMarkPaidDialog({ ...markPaidDialog, accountId: v })}
            />
            <div className="space-y-2">
              <Label>{t('dialogs.paymentRef')}</Label>
              <Input
                value={markPaidDialog.ref}
                onChange={(e) => setMarkPaidDialog({ ...markPaidDialog, ref: e.target.value })}
                placeholder={t('dialogs.paymentRefPlaceholder')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMarkPaidDialog({ open: false, method: 'BANK_TRANSFER', ref: '' })}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              onClick={() =>
                id &&
                markPaidMutation.mutate({
                  id,
                  paymentMethod: markPaidDialog.method,
                  paymentRef: markPaidDialog.ref || undefined,
                  accountId: markPaidDialog.accountId,
                })
              }
              disabled={markPaidMutation.isPending}
            >
              {markPaidMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('actions.markAsPaid')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
